// src/components/MermaidDiagram.tsx
//
// Renders a Mermaid syntax string to an inline SVG diagram. Used by
// CodeBlock whenever it encounters a ```mermaid fence. Fails gracefully —
// invalid Mermaid syntax shows a readable error + the raw source instead
// of crashing the message bubble.
import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

let initialized = false;
function ensureInitialized() {
  if (initialized) return;
  mermaid.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'strict' });
  initialized = true;
}

interface Props {
  code: string;
}

export default function MermaidDiagram({ code }: Props) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    ensureInitialized();
    let cancelled = false;
    setError(null);
    setSvg(null);

    mermaid.parse(code)
      .then(() => mermaid.render(idRef.current, code))
      .then((result) => {
        if (!cancelled) setSvg(result.svg);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not render this diagram.');
      });

    return () => { cancelled = true; };
  }, [code]);

  if (error) {
    return (
      <div className="diagram-error">
        <p>⚠ Couldn't render this diagram: {error}</p>
        <pre>{code}</pre>
      </div>
    );
  }

  if (!svg) {
    return <div className="diagram-loading">Rendering diagram…</div>;
  }

  // Mermaid's own output is sanitized SVG (securityLevel: 'strict' above),
  // so this injection is safe.
  return <div className="mermaid-diagram" dangerouslySetInnerHTML={{ __html: svg }} />;
}